import React from 'react';
import { Check, CircleAlert, Eye, LockKeyhole, Route, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import AutopilotRun from '../components/AutopilotRun';
import { ClosingCta, PrimaryCta, Reveal, SectionIntro } from '../components/Alt4Shared';

const boundaries = [
  { icon: SlidersHorizontal, title: 'Rules', body: 'Define which goals, conditions, and location policies shape each run.' },
  { icon: LockKeyhole, title: 'Permissions', body: 'Scope which records and actions are available to each workflow.' },
  { icon: ShieldCheck, title: 'Approvals', body: 'Pause at decision points where your team needs to review or authorize.' },
  { icon: CircleAlert, title: 'Exceptions', body: 'Route ambiguity with the relevant history, reason, and next step attached.' },
];

export default function Alt4Autopilot() {
  return (
    <Alt4Layout title="Autopilot">
        <section className="alt4-page-hero alt4-section">
          <div className="alt4-container alt4-hero-grid alt4-page-hero__grid">
            <Reveal className="alt4-hero-copy">
              <span className="alt4-eyebrow">Governed autonomy</span>
              <h1>Autonomy with boundaries.</h1>
              <p>Asha monitors live operations, chooses the next step, takes action inside the PMS, and keeps going until the work is complete—or a person needs to decide.</p>
              <PrimaryCta />
            </Reveal>
            <Reveal className="alt4-panel alt4-autopilot-hero-panel" delay={0.08}>
              <header><span className="alt4-interface-label">Autopilot control</span><span className="alt4-status alt4-status--running">On</span></header>
              <div className="alt4-autopilot-goal"><Route aria-hidden="true" /><span><small>Current goal</small><strong>Resolve tomorrow’s readiness gaps</strong></span></div>
              <div className="alt4-autopilot-metrics">
                <div><span>Observe</span><strong>Live practice state</strong></div>
                <div><span>Act</span><strong>Within permission scope</strong></div>
                <div><span>Verify</span><strong>Before continuing</strong></div>
              </div>
              <div className="alt4-autopilot-review"><Eye aria-hidden="true" /><span><strong>Human review stays available</strong>Every action and decision remains visible.</span></div>
            </Reveal>
          </div>
        </section>

        <section className="alt4-section alt4-section--pale">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="A continuous operating loop"
              title="Give the system a goal. Keep the team in control."
              body="Autopilot connects reasoning to action and action to verification. Each result becomes new context for the next step, so a run can continue without losing its history."
            />
            <AutopilotRun />
          </div>
        </section>

        <section className="alt4-section">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="Control plane"
              title="Boundaries are part of the workflow, not an afterthought."
              body="Operational control travels with the work. Your organization can set shared policy, narrow it by location, and decide where review belongs."
            />
            <div className="alt4-grid alt4-grid--four alt4-governance-grid">
              {boundaries.map(({ icon: Icon, title, body }, index) => (
                <Reveal className="alt4-card alt4-governance-card" delay={index * 0.05} key={title}>
                  <Icon aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="alt4-section alt4-section--blue">
          <div className="alt4-container alt4-grid alt4-grid--two alt4-audit-section">
            <SectionIntro
              inverse
              eyebrow="Visible by design"
              title="Know what happened, why it happened, and what comes next."
              body="A complete activity trail connects the triggering event, the context used, the rule applied, the action taken, and the resulting state."
            />
            <div className="alt4-panel alt4-panel--blue alt4-audit-trail">
              <span className="alt4-interface-label">Activity trail</span>
              {['Read the current schedule and readiness state', 'Applied location rules and action permissions', 'Completed authorized preparation tasks', 'Routed one exception with supporting context'].map((item, index) => (
                <div key={item}><span>{index + 1}</span><p>{item}</p>{index < 3 ? <Check aria-hidden="true" /> : <CircleAlert aria-hidden="true" />}</div>
              ))}
            </div>
          </div>
        </section>

        <ClosingCta
          eyebrow="Put operations in motion"
          title="See how governed autopilot carries work to completion."
          body="Explore the controls, task runs, exception handling, and activity trail in a guided walkthrough."
        />
    </Alt4Layout>
  );
}
